/* eslint-disable import/first */
import type { ReactNode } from 'react';
import Language from '@mui/icons-material/Language';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MoreButton from 'src/components/MoreButton';
import { updateUserLang } from 'src/backend/user';
import { LANG_SETTINGS } from 'src/config';
import useAuth from 'src/hooks/useAuth';
import useLocales from 'src/hooks/useLocales';

type AuthLayoutProps = {
  children?: ReactNode;
};

type SwitchLanguageButtonProps = {
  excludeLanguages?: string[];
  containerProps?: Record<string, unknown>;
  [key: string]: unknown;
};

export function SwitchLanguageButton({
  excludeLanguages = [],
  containerProps,
  ...props
}: SwitchLanguageButtonProps) {
  const MoreButtonComponent = MoreButton as any;
  const { user, invalidateUserData } = useAuth();
  const { t } = useTranslation();
  const { allLang: langs, onChangeLang } = useLocales();
  const allLang = langs.filter(
    (lang: { value: string }) => !excludeLanguages.includes(lang.value)
  );

  if (!LANG_SETTINGS) {
    return null;
  }

  return (
    <Box
      sx={{ position: 'absolute', right: 8, top: 8, zIndex: 100 }}
      {...containerProps}
    >
      <MoreButtonComponent
        options={allLang.map(
          ({ label, value }: { label: string; value: string }) => ({
            text: label,
            action: async () => {
              onChangeLang(value);
              if (user) {
                updateUserLang({
                  lang: value,
                })
                  .then(() => {
                    invalidateUserData();
                  })
                  .catch(() => {});
              }
            },
          })
        )}
        Icon={Language}
        aria-label={t('auth.language-switcher.change-language')}
        aria-haspopup="true"
        role="button"
        tooltipTitle={t('auth.language-switcher.select-preferred-language')}
        {...props}
      />
    </Box>
  );
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <Box sx={{ height: '100%' }}>
      <SwitchLanguageButton />
      {children}
    </Box>
  );
}
